<template>
    <fieldset>
        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Miljö</div>
            </div>
            <select
                class="custom-select"
                required
                v-model="environment"
                @change="onUpdate"
            >
                <option value="production">Produktionsmiljö</option>
                <option value="staging">Stagingmiljö</option>
                <option value="playground">Testmiljö</option>
            </select>
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Swish nummer</div>
            </div>
            <input
                type="number"
                class="form-control"
                v-model="swishNumber"
                @input="onUpdate"
                :required="!bringYourOwnKeys"
            />
        </div>

        <div class="mb-3">
            <div class="input-group">
                <div class="input-group-prepend">
                    <div class="input-group-text">Nyckelkälla</div>
                </div>
                <select
                    class="custom-select"
                    v-model="bringYourOwnKeys"
                    @change="onUpdate"
                    required
                >
                    <option :value="false">Använd våra nycklar</option>
                    <option :value="true">Ta med egna nycklar</option>
                </select>
            </div>
        </div>

        <template v-if="bringYourOwnKeys">
            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <div class="input-group-text">Certificate</div>
                </div>
                <input
                    type="file"
                    class="form-control"
                    :required="!certificate"
                    v-on:change="handleCertificateFile"
                />
            </div>

            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <div class="input-group-text">Private Key</div>
                </div>
                <input
                    type="file"
                    class="form-control"
                    :required="!privateKey"
                    v-on:change="handlePrivateKeyFile"
                />
            </div>

            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <div class="input-group-text">Password</div>
                </div>
                <input
                    type="password"
                    class="form-control"
                    required
                    v-model="password"
                    @input="onUpdate"
                />
            </div>
        </template>

        <div class="mb-3">
            <div class="input-group">
                <div class="input-group-prepend">
                    <div class="input-group-text">Hantera betalningar</div>
                </div>

                <select
                    class="custom-select"
                    v-model="handleRecurring"
                    @change="onUpdate"
                    required
                >
                    <option :value="false">Nej</option>
                    <option :value="true">Ja</option>
                </select>
            </div>
            <div class="alert alert-info small mt-2">
                Vera tipsar: Ni måste även aktivera modulen Abonnemang under
                Inställningar/Utskicksprofiler/Erbjudande.
            </div>
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: ConsentID</div>
            </div>
            <input
                type="text"
                class="form-control"
                required
                v-model="fieldConsentId"
                @input="onUpdate"
            />
        </div>
    </fieldset>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch } from "vue";
import { route } from "ziggy";
const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  }
});
const {
  modelValue
} = toRefs(props);
const emit = defineEmits(["update:modelValue"]);
const environment = ref(null);
const certificate = ref(null);
const privateKey = ref(null);
const password = ref(null);
const fieldConsentId = ref(null);
const encryptingCertificate = ref(false);
const encryptingPrivateKey = ref(false);
const handleRecurring = ref(false);
const bringYourOwnKeys = ref(false);
const swishNumber = ref(null);
const encryptingKey = ref(null);
const serializedValue = computed(() => {
  return {
    environment: environment.value,
    certificate: certificate.value,
    private_key: privateKey.value,
    password: password.value,
    field_consent_id: fieldConsentId.value,
    handle_recurring: handleRecurring.value,
    bring_your_own_keys: bringYourOwnKeys.value,
    swish_number: swishNumber.value
  };
});
function onUpdate() {
    emit("update:modelValue", serializedValue.value);
}
async function handleCertificateFile(event) {
  encryptingCertificate.value = true;
  const encryptedFile = await handleFile(event);
  encryptingCertificate.value = false;
  if (!encryptedFile) {
    return;
  }
  certificate.value = encryptedFile;
  onUpdate();
}
async function handlePrivateKeyFile(event) {
  encryptingKey.value = true;
  const encryptedFile = await handleFile(event);
  encryptingKey.value = false;
  if (!encryptedFile) {
    return;
  }
  privateKey.value = encryptedFile;
  onUpdate();
}
async function handleFile(event) {
  if (event.target.files.length < 0) {
    return false;
  }
  const formData = new FormData();
  formData.append("file", event.target.files[0]);
  try {
    const response = await axios.post(route("utilities.encryptFile"), formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    return response.data.encryptedFile;
  } catch (error) {
    console.error("Upload failed:", error);
    return false;
  }
}
watch(modelValue, () => {
  if (JSON.stringify(modelValue.value) !== JSON.stringify(serializedValue.value)) {
    environment.value = modelValue.value.environment;
    certificate.value = modelValue.value.certificate;
    privateKey.value = modelValue.value.private_key;
    password.value = modelValue.value.password;
    fieldConsentId.value = modelValue.value.field_consent_id;
    handleRecurring.value = modelValue.value.handle_recurring ?? false;
    bringYourOwnKeys.value = modelValue.value.bring_your_own_keys ?? false;
    swishNumber.value = modelValue.value.swish_number ?? null;
    if (JSON.stringify(modelValue.value) !== JSON.stringify(serializedValue.value)) {
      onUpdate();
    }
  }
}, {
  immediate: true
});
</script>
